import { motion } from "framer-motion";
import { ShoppingBag, LayoutDashboard, MapPin, ChefHat, BarChart3, Check } from "lucide-react";
import { FEATURES, scrollToSection } from "../lib/site";

const ICONS = { ShoppingBag, LayoutDashboard, MapPin, ChefHat, BarChart3 };

export default function Features() {
  return (
    <section id="products" data-testid="products-section" className="relative py-24 lg:py-36 bg-[#0b0c10] overflow-hidden">
      <div className="pointer-events-none absolute top-1/3 -left-48 w-[560px] h-[560px] rounded-full bg-amber-500/8 blur-[160px]" />
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <motion.div
          className="max-w-2xl mb-16"
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8 }}
        >
          <p className="eyebrow mb-5">Products &amp; Features</p>
          <h2 data-testid="products-headline" className="font-display font-semibold text-white tracking-tight leading-[1.05] text-3xl sm:text-5xl">
            Five pillars. <span className="italic text-amber-500">One platform.</span>
          </h2>
          <p className="mt-6 text-base sm:text-lg text-gray-400 leading-relaxed">
            Everything an independent food business needs to take orders online, run the kitchen,
            dispatch drivers and grow — without paying aggregators a cut of every sale.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-5 lg:gap-6">
          {FEATURES.map((f, i) => {
            const Icon = ICONS[f.icon];
            return (
              <motion.article
                key={f.id}
                data-testid={`feature-card-${f.id}`}
                className={`${f.span} group relative rounded-2xl bg-[#171923] border border-amber-500/15 hover:border-amber-500/50 p-7 sm:p-9 overflow-hidden transition-colors duration-300`}
                initial={{ opacity: 0, y: 36 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ delay: 0.06 * i, duration: 0.7 }}
                whileHover={{ y: -4 }}
              >
                <div className="pointer-events-none absolute -top-20 -right-20 w-56 h-56 rounded-full bg-amber-500/0 group-hover:bg-amber-500/10 blur-3xl transition-colors duration-500" />
                <div className="flex items-start justify-between gap-4">
                  <span className="font-mono-x text-xs tracking-[0.3em] text-amber-500/70">{f.number}</span>
                  <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-amber-500/10 border border-amber-500/25 text-amber-400">
                    <Icon className="w-5 h-5" />
                  </div>
                </div>
                <h3 className="mt-6 font-display text-2xl sm:text-3xl font-semibold text-white">{f.title}</h3>
                <p className="mt-2 font-mono-x text-[11px] tracking-[0.15em] uppercase text-amber-500/90">{f.tagline}</p>
                <p className="mt-5 text-sm sm:text-base text-gray-400 leading-relaxed max-w-xl">{f.description}</p>
                <ul className="mt-6 space-y-2.5">
                  {f.highlights.map((h) => (
                    <li key={h} className="flex items-center gap-3 text-sm text-gray-300">
                      <Check className="w-4 h-4 text-amber-500 shrink-0" />
                      {h}
                    </li>
                  ))}
                </ul>
              </motion.article>
            );
          })}
        </div>

        <motion.div
          className="mt-14 flex justify-center"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.2, duration: 0.8 }}
        >
          <button
            data-testid="products-cta-button"
            onClick={() => scrollToSection("contact")}
            className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-400 text-[#0b0c10] font-bold px-8 py-4 rounded-full shadow-[0_0_30px_rgba(245,158,11,0.35)] transition-colors duration-200"
          >
            See it on your shop
          </button>
        </motion.div>
      </div>
    </section>
  );
}
